import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogIsUniqueConstructionTypeFieldsGql } from '../catalogConstructionTypesQueries';
import { CONSTRUCTION_TYPE_NAME_FIELDS, trimConstructionTypeNames } from '../helpers';
import type { IConstructionTypeNames, TConstructionTypeNameField } from '../types';

const requestId = 'isUniqueConstructionTypeFields';

interface IUseConstructionTypeNamesUniqueness {
    values: IConstructionTypeNames;
    initialValues: IConstructionTypeNames;
    catalogConstructionTypeId?: number;
}

export const useConstructionTypeNamesUniqueness = ({
    values,
    initialValues,
    catalogConstructionTypeId,
}: IUseConstructionTypeNamesUniqueness) => {
    const dispatch = useDispatch();
    const [checkedKey, setCheckedKey] = useState<string | null>(null);

    const [resetUniqueData] = useResetStoreData({ requestId });

    const [isUniqueData, isUniqueStatus, , isUniqueRequest] = useRequest({
        requestId,
        query: catalogIsUniqueConstructionTypeFieldsGql,
        withEffect: false,
    });

    useEffect(() => {
        resetUniqueData(undefined);
    }, [resetUniqueData]);

    const { name, fullName, shortName, contractFileName } = values;

    const trimmedValues = useMemo(
        () => trimConstructionTypeNames({ name, fullName, shortName, contractFileName }),
        [contractFileName, fullName, name, shortName],
    );

    const valuesKey = JSON.stringify(trimmedValues);
    const isAllFilled = CONSTRUCTION_TYPE_NAME_FIELDS.every(({ field }) => Boolean(trimmedValues[field]));
    const isInitial = CONSTRUCTION_TYPE_NAME_FIELDS.every(({ field }) => trimmedValues[field] === initialValues[field]);

    const debouncedCheck = useDebounce(() => {
        if (!isAllFilled || isInitial || valuesKey === checkedKey) {
            return;
        }

        dispatch(isUniqueRequest({ catalogConstructionTypeId, ...trimmedValues }));
        setCheckedKey(valuesKey);
    }, 500);

    useEffect(() => {
        debouncedCheck();
    }, [debouncedCheck, valuesKey]);

    const checkResult = isUniqueData?.catalogIsUniqueConstructionTypeFields;
    const isPending = !isInitial && (valuesKey !== checkedKey || isUniqueStatus === RequestStatus.FETCHING);

    const notUniqueFields = useMemo<TConstructionTypeNameField[]>(() => {
        if (isInitial || isPending || !checkResult || checkResult.result) {
            return [];
        }

        return CONSTRUCTION_TYPE_NAME_FIELDS.map(({ field }) => field).filter(
            (field) => checkResult.validation?.some(({ property }) => property?.toLowerCase() === field.toLowerCase()),
        );
    }, [checkResult, isInitial, isPending]);

    const isAllUnique = isAllFilled && (isInitial || (!isPending && Boolean(checkResult) && !notUniqueFields.length));

    return { notUniqueFields, isAllUnique };
};
